import { Button, zLayer } from "@sellar-io/components";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useSellarSession } from "../../hooks/useSellarSession";
import {
  getSessionTestFailure,
  setSessionTestFailure,
  type SessionTestFailure,
} from "../../hooks/sessionTestControls";

const scenarios = [
  { value: "none", label: "No simulated failure" },
  { value: "authentication", label: "Firebase session initialization" },
  { value: "session", label: "Sellar session loading" },
  { value: "user", label: "New profile creation" },
] as const satisfies readonly { value: SessionTestFailure; label: string }[];

export const SessionTestPanel = () => {
  const { firebase, state } = useSellarSession();
  const [failure, setFailure] = useState(getSessionTestFailure);
  const [storageError, setStorageError] = useState(false);
  const changeFailure = (next: SessionTestFailure) => {
    try {
      setSessionTestFailure(next);
      setFailure(next);
      setStorageError(false);
    } catch {
      setStorageError(true);
    }
  };
  const restarting =
    firebase.loading ||
    state.status === "retrying-initialization" ||
    state.status === "loading-user" ||
    state.status === "loading-business";

  return createPortal(
    <details
      className={`${zLayer.dev} fixed right-3 bottom-3 w-80 max-w-[calc(100vw-1.5rem)] rounded-lg border border-zinc-300 bg-white text-sm text-zinc-800 shadow-lg`}
    >
      <summary className="cursor-pointer px-4 py-3 font-semibold">
        Session testing{failure !== "none" ? " · failure active" : ""}
      </summary>
      <div className="flex max-h-[min(65dvh,calc(100dvh-5rem))] flex-col gap-3 overflow-auto border-t border-zinc-200 p-4">
        <p>
          Local and staging only. Sign in normally first, then choose a failure
          and restart your session.
        </p>
        <label htmlFor="session-test-failure" className="font-semibold">
          Simulate a failure
        </label>
        <select
          id="session-test-failure"
          className="w-full rounded border border-zinc-300 bg-white p-2"
          value={failure}
          onChange={(event) => {
            const scenario = scenarios.find(
              ({ value }) => value === event.target.value,
            );
            if (scenario) changeFailure(scenario.value);
          }}
        >
          {scenarios.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {storageError && (
          <p role="alert">
            Session storage is unavailable. Allow browser storage to use these
            controls.
          </p>
        )}
        <Button size="small" onClick={firebase.retry} disabled={restarting}>
          Restart session
        </Button>
        <Button
          className="w-full"
          size="small"
          color="secondary"
          onClick={() => changeFailure("none")}
          disabled={failure === "none"}
        >
          Clear failure
        </Button>
        <p>
          Clear the failure, then use <strong>Try again</strong> in the login
          card to test recovery.
        </p>
        <p className="text-xs text-zinc-500">
          Applies only to this tab and survives refresh. Simulated failures are
          not reported to Sentry.
        </p>
      </div>
    </details>,
    document.body,
  );
};
